import type { FlowNodeRow } from "@/types/flow"

export const FLOW_HISTORY_LIMIT = 100

export type FlowPoint = { x: number; y: number }

export type FlowHistoryEntry =
  | { type: "move"; nodeId: number; from: FlowPoint; to: FlowPoint }
  | {
      type: "resize"
      nodeId: number
      from: number | null
      to: number | null
      /** Есть, если тянули за левый или верхний край и нода сдвинулась. */
      position?: { from: FlowPoint; to: FlowPoint }
    }
  | { type: "create"; nodeId: number }
  | { type: "delete"; nodeId: number }
  | { type: "connect"; edgeId: number }
  | { type: "disconnect"; edgeId: number }

/** Групповой сдвиг или удаление — один шаг undo. */
export type FlowHistoryCommand = FlowHistoryEntry | { type: "batch"; commands: FlowHistoryEntry[] }

export type FlowHistoryState = {
  past: FlowHistoryCommand[]
  future: FlowHistoryCommand[]
  deletedIds: number[]
  deletedEdgeIds: number[]
}

export type FlowHistoryPush = {
  state: FlowHistoryState
  purgeIds: number[]
  purgeEdgeIds: number[]
}

export function emptyFlowHistory(): FlowHistoryState {
  return { past: [], future: [], deletedIds: [], deletedEdgeIds: [] }
}

export function pushFlowHistory(state: FlowHistoryState, command: FlowHistoryCommand): FlowHistoryPush {
  const past = state.past.concat(command)
  while (past.length > FLOW_HISTORY_LIMIT) past.shift()
  return dropUnreferenced({
    past,
    future: [],
    deletedIds: applyNodeTombstone(state.deletedIds, command, "forward"),
    deletedEdgeIds: applyEdgeTombstone(state.deletedEdgeIds, command, "forward"),
  })
}

export function undoFlowHistory(
  state: FlowHistoryState,
): { state: FlowHistoryState; command: FlowHistoryCommand } | null {
  const command = state.past[state.past.length - 1]
  if (!command) return null
  const past = state.past.slice(0, -1)
  const future = state.future.concat(command)
  return {
    state: {
      past,
      future,
      deletedIds: applyNodeTombstone(state.deletedIds, command, "undo"),
      deletedEdgeIds: applyEdgeTombstone(state.deletedEdgeIds, command, "undo"),
    },
    command,
  }
}

export function redoFlowHistory(
  state: FlowHistoryState,
): { state: FlowHistoryState; command: FlowHistoryCommand } | null {
  const command = state.future[state.future.length - 1]
  if (!command) return null
  const future = state.future.slice(0, -1)
  const past = state.past.concat(command)
  return {
    state: {
      past,
      future,
      deletedIds: applyNodeTombstone(state.deletedIds, command, "forward"),
      deletedEdgeIds: applyEdgeTombstone(state.deletedEdgeIds, command, "forward"),
    },
    command,
  }
}

/** Серверный список не должен возвращать мягко удалённую ноду и не должен стирать только что восстановленную. */
export function mergeFlowNodeRows(
  prev: FlowNodeRow[],
  incoming: FlowNodeRow[],
  deletedIds: readonly number[],
  deletedEdgeIds: readonly number[] = [],
): FlowNodeRow[] {
  const deleted = new Set(deletedIds)
  const deletedEdges = new Set(deletedEdgeIds)
  const prevById = new Map(prev.map((node) => [node.id, node]))
  const fromServer = incoming
    .filter((node) => !deleted.has(node.id))
    .map((node) => mergeNodeInputs(node, prevById.get(node.id), deletedEdges))
  const serverIds = new Set(fromServer.map((node) => node.id))
  const keptLocal = prev
    .filter((node) => !deleted.has(node.id) && !serverIds.has(node.id))
    .map((node) => stripDeletedInputs(node, deletedEdges))
  return fromServer.concat(keptLocal).sort((a, b) => a.id - b.id)
}

function dropUnreferenced(state: FlowHistoryState): FlowHistoryPush {
  const live = state.past.concat(state.future)
  const purgeIds = state.deletedIds.filter((id) => !references(live, id))
  const purgeEdgeIds = state.deletedEdgeIds.filter((id) => !referencesEdge(live, id))
  if (purgeIds.length === 0 && purgeEdgeIds.length === 0) return { state, purgeIds, purgeEdgeIds }
  const dropping = new Set(purgeIds)
  const droppingEdges = new Set(purgeEdgeIds)
  return {
    state: {
      ...state,
      deletedIds: state.deletedIds.filter((id) => !dropping.has(id)),
      deletedEdgeIds: state.deletedEdgeIds.filter((id) => !droppingEdges.has(id)),
    },
    purgeIds,
    purgeEdgeIds,
  }
}

function leavesOf(command: FlowHistoryCommand, direction: "forward" | "undo"): FlowHistoryEntry[] {
  if (command.type !== "batch") return [command]
  return direction === "undo" ? [...command.commands].reverse() : command.commands
}

function applyNodeTombstone(ids: number[], command: FlowHistoryCommand, direction: "forward" | "undo"): number[] {
  let next = ids
  for (const leaf of leavesOf(command, direction)) {
    if (leaf.type !== "create" && leaf.type !== "delete") continue
    const hide = direction === "forward" ? leaf.type === "delete" : leaf.type === "create"
    next = hide ? withId(next, leaf.nodeId) : withoutId(next, leaf.nodeId)
  }
  return next
}

function applyEdgeTombstone(ids: number[], command: FlowHistoryCommand, direction: "forward" | "undo"): number[] {
  let next = ids
  for (const leaf of leavesOf(command, direction)) {
    if (leaf.type !== "connect" && leaf.type !== "disconnect") continue
    const hide = direction === "forward" ? leaf.type === "disconnect" : leaf.type === "connect"
    next = hide ? withId(next, leaf.edgeId) : withoutId(next, leaf.edgeId)
  }
  return next
}

function mergeNodeInputs(server: FlowNodeRow, prev: FlowNodeRow | undefined, deletedEdges: Set<number>): FlowNodeRow {
  const serverInputs = (server.inputs ?? []).filter((input) => !deletedEdges.has(input.id))
  const known = new Set(serverInputs.map((input) => input.id))
  const serverLinks = new Set(serverInputs.map(flowInputLinkKey))
  // Оптимистический вход живёт с временным id, пока сервер не прислал то же ребро.
  const localOnly = (prev?.inputs ?? []).filter(
    (input) => !deletedEdges.has(input.id) && !known.has(input.id) && !serverLinks.has(flowInputLinkKey(input)),
  )
  const inputs = serverInputs.concat(localOnly).sort((a, b) => a.position - b.position || a.id - b.id)
  return { ...server, inputs }
}

function flowInputLinkKey(input: NonNullable<FlowNodeRow["inputs"]>[number]): string {
  return `${input.source_node_id}:${input.source_port ?? "image"}:${input.target_port ?? "references"}`
}

function stripDeletedInputs(node: FlowNodeRow, deletedEdges: Set<number>): FlowNodeRow {
  if (deletedEdges.size === 0 || !node.inputs?.length) return node
  const inputs = node.inputs.filter((input) => !deletedEdges.has(input.id))
  return inputs.length === node.inputs.length ? node : { ...node, inputs }
}

function references(commands: FlowHistoryCommand[], nodeId: number): boolean {
  return commands.some((command) =>
    leavesOf(command, "forward").some(
      (leaf) => (leaf.type === "create" || leaf.type === "delete") && leaf.nodeId === nodeId,
    ),
  )
}

function referencesEdge(commands: FlowHistoryCommand[], edgeId: number): boolean {
  return commands.some((command) =>
    leavesOf(command, "forward").some(
      (leaf) => (leaf.type === "connect" || leaf.type === "disconnect") && leaf.edgeId === edgeId,
    ),
  )
}

function withId(ids: number[], id: number): number[] {
  return ids.includes(id) ? ids : ids.concat(id)
}

function withoutId(ids: number[], id: number): number[] {
  return ids.filter((item) => item !== id)
}
